import { Injectable } from '@angular/core';
import * as echarts from 'echarts';
import { SeriesOption } from 'echarts';
import { YAXisOption } from 'echarts/types/dist/shared';
import { AxisSettings, GlobalSettings, TimeRangeInterface } from '@twaice-fe/shared/models';
import { getDisplayValues, getOffsetMs, resolveTimezone, SensorTypeFormatterService } from '@twaice-fe/frontend/shared/utilities';

const VALUE_PRECISION = 4;

export interface ChartConfigParams {
  hasData: boolean;
  filteredSensors: [string, any][];
  filteredAxis: AxisSettings[];
  globalSettings: GlobalSettings;
  axisCount: number;
  axisSpacing: number;
  timeRange: TimeRangeInterface | null | undefined;
  minDate: Date;
  maxDate: Date;
  unitMinMax: Record<string, { min: number; max: number }>;
  chartIndex: number;
  emptyLineColors: string[];
  lineColors: string[];
  sensors: Record<string, any>;
  showLegend?: boolean;
  legendOrient?: 'horizontal' | 'vertical' | null;
  showToolbox?: boolean;
}

export interface ChartProcessingResult {
  unitMinMax: Record<string, { min: number; max: number }>;
  minDate: Date;
  maxDate: Date;
  filteredAxis: AxisSettings[];
  filteredSensors: [string, any][];
  hasData: boolean;
}

@Injectable()
export class ChartDeService {
  private resolvedTimezone: string | null = null;
  private systemTimezone: string | null = null;
  private userTimezone: string | null = null;
  private cachedFormatter: Intl.DateTimeFormat | null = null;
  private cachedFormatterTz: string | null = null;
  private cachedTooltipFormatter: Intl.DateTimeFormat | null = null;
  private cachedTooltipFormatterTz: string | null = null;
  private cachedDatePartsFormatters = new Map<string, Intl.DateTimeFormat>();

  constructor(private sensorTypeFormatter: SensorTypeFormatterService) {}

  private get tz(): string | null {
    return this.resolvedTimezone;
  }

  setTimezoneContext(opts: { userTimezone: string | null; systemTimezone: string | null }): void {
    this.userTimezone = opts.userTimezone;
    this.systemTimezone = opts.systemTimezone;
    this.resolvedTimezone = resolveTimezone(this.userTimezone, this.systemTimezone);
  }

  createChartOption(config: ChartConfigParams): echarts.EChartsOption {
    const {
      hasData,
      filteredSensors,
      filteredAxis,
      globalSettings,
      axisCount,
      axisSpacing,
      timeRange,
      minDate,
      maxDate,
      unitMinMax,
      chartIndex,
      emptyLineColors,
      lineColors,
      sensors,
      showLegend,
      legendOrient: legendOrientOverride,
      showToolbox,
    } = config;

    const series: SeriesOption[] = hasData
      ? this.createDataSeries(filteredSensors, filteredAxis, globalSettings, lineColors)
      : this.createEmptySeries(filteredSensors, emptyLineColors);

    const yAxes: YAXisOption[] = hasData
      ? this.createYAxes(filteredAxis, globalSettings, axisCount, axisSpacing, unitMinMax, chartIndex, filteredSensors)
      : [];

    // Add forecast markArea if there's forecast data
    const forecastMarkArea = this.createForecastMarkArea(filteredSensors, maxDate);

    // Add DST switch markLines
    const dstMarkLines = this.createDstMarkLines(minDate, maxDate);

    let startDate = null;
    let endDate = null;
    if (timeRange) {
      startDate = new Date(timeRange?.value[0]);
      endDate = new Date(timeRange?.value[1]);
    }

    const effectiveOrient = legendOrientOverride ?? (globalSettings.chartMode === 'single' ? 'horizontal' : 'vertical');
    const isHorizontalLegend = effectiveOrient === 'horizontal';

    return {
      useUTC: !!this.tz,
      grid: {
        left: `${16 + Math.ceil((globalSettings.chartMode === 'single' ? axisCount : 1) / 2) * axisSpacing}`,
        right: `${
          16 +
          Math.floor((globalSettings.chartMode === 'single' ? axisCount : 1) / 2) * axisSpacing +
          (isHorizontalLegend || showLegend === false ? 0 : 230)
        }`,
        bottom: 75,
        top: isHorizontalLegend ? 76 : 48,
        containLabel: false,
      },
      xAxis:
        !hasData || yAxes.length > 0
          ? [
              {
                type: 'time',
                boundaryGap: [0, '100%'],
                min: this.toZonedMs(startDate || minDate),
                max: this.toZonedMs(endDate || maxDate),
                axisLabel: {
                  hideOverlap: true,
                  color: '#9B9FA8',
                },
                nameTextStyle: {
                  color: '#9B9FA8',
                },
              },
            ]
          : [],
      yAxis: hasData
        ? yAxes
        : {
            show: true,
            axisLine: {
              show: true,
            },
          },
      tooltip: {
        show: hasData,
        trigger: hasData ? 'axis' : undefined,
        confine: hasData ? true : undefined,
        formatter: hasData
          ? (params: echarts.DefaultLabelFormatterCallbackParams[] | echarts.DefaultLabelFormatterCallbackParams) => {
              let typedParams: echarts.DefaultLabelFormatterCallbackParams[];
              if (Array.isArray(params)) {
                typedParams = params;
              } else {
                typedParams = [params];
              }

              const formattedDate = this.formatTooltipDateWithTimezone(
                new Date((typedParams[0].data as any)?.originalTs ?? this.fromZonedMs(typedParams[0].value[0]))
              );
              let result = formattedDate + '<br/>';
              // De-duplicate entries with same sensor name and timestamp
              const uniqueParams = typedParams.reduce((acc, param) => {
                const key = `${param.seriesName}_${param.value[0]}`;
                if (!acc.has(key)) {
                  acc.set(key, param);
                }
                return acc;
              }, new Map());

              uniqueParams.forEach((param) => {
                const colorMarker = `<span style="display:inline-block;margin-right:5px;border-radius:50%;width:10px;height:10px;background-color:${param.color};"></span>`;

                // Find the sensor data to get scaling factor
                const sensorData = filteredSensors.find(([name]) => name === param.seriesName)?.[1];
                const scalingFactor = sensorData?.scalingFactor;
                const unit = sensorData?.unit || '';
                const value = parseFloat(param.value[1]);

                // Apply unit scaling and value scaling
                const { displayName, displayValue } = getDisplayValues(
                  sensorData?.displayName ?? param.seriesName,
                  scalingFactor,
                  unit,
                  value
                );
                // The tooltip is rendered as HTML and the name can contain user-defined custom names
                result += `${colorMarker} ${echarts.format.encodeHTML(displayName)}: <b>${parseFloat(
                  displayValue.toFixed(VALUE_PRECISION)
                )}</b><br/>`;
              });
              return result;
            }
          : undefined,
      },
      legend: {
        show: showLegend ?? true,
        type: 'scroll',
        orient: effectiveOrient,
        data: filteredSensors.map((sensor) => sensor[0]),
        top: 40,
        left: isHorizontalLegend ? 16 : undefined,
        right: isHorizontalLegend ? undefined : 8,
        selected: filteredSensors.reduce(
          (acc, [name, sensor]) => {
            acc[name] = hasData ? sensor.data && sensor.data.length > 0 : false;
            return acc;
          },
          {} as Record<string, boolean>
        ),
        textStyle: {
          color: '#9B9FA8',
          width: isHorizontalLegend ? undefined : 180,
          overflow: isHorizontalLegend ? 'none' : 'break',
          rich: {
            emptyData: {
              color: '#9B9FA8',
              opacity: 0.7,
            },
          },
        },
        formatter: (name) => {
          const sensor = hasData ? sensors[name] : filteredSensors.find((s) => s[0] === name)?.[1];
          // Apply unit scaling to legend name
          const { displayName } = getDisplayValues(
            sensor?.displayName ?? name,
            sensor?.scalingFactor,
            sensor?.unit,
            sensor?.value
          );
          // ECharts parses the legend label as rich text and the name can contain user-defined
          // custom names: strip the rich-text control characters so they can't alter the label
          const safeDisplayName = displayName.replace(/[{}|]/g, '');
          if (!sensor || !sensor.data || sensor.data.length === 0) {
            return '{emptyData|' + safeDisplayName + '}';
          }

          return safeDisplayName;
        },
      },
      ...(hasData && {
        ...((showToolbox ?? true) && {
          toolbox: this.createToolboxConfig(chartIndex, yAxes, {
            onSingleItemTool: () => {},
            onPanIndicator: () => {},
            onResetTool: () => {},
            onDownloadCSV: () => {},
          }),
        }),
        dataZoom: this.createDataZoomConfig(),
      }),
      series: [...series, forecastMarkArea, dstMarkLines].filter(Boolean),
    };
  }

  createDataSeries(
    filteredSensors: [string, any][],
    filteredAxis: AxisSettings[],
    globalSettings: GlobalSettings,
    lineColors: string[]
  ): SeriesOption[] {
    const series: SeriesOption[] = [];
    const now = new Date();

    filteredSensors.forEach(([sensorName, sensorData], index) => {
      const yAxisIndex = this.getAxisIndex(sensorData, filteredAxis, globalSettings);
      const chartType = filteredAxis[yAxisIndex]?.chartType || 'line';
      const lineStyle = filteredAxis[yAxisIndex]?.lineStyle || 'linear';
      const color = lineColors[index % lineColors.length];

      const shiftedData =
        sensorData.data?.map((point: any) => {
          const originalTs = new Date(point[0]).getTime();
          const shiftedTs = this.toZonedMs(new Date(originalTs));
          return { value: [shiftedTs, point[1]], originalTs };
        }) || [];

      // Separate historical and forecast data points using original timestamp
      const historicalData = shiftedData.filter((p: any) => p.originalTs <= now.getTime());
      const forecastData = shiftedData.filter((p: any) => p.originalTs > now.getTime());

      // Add last historical point to forecast data to connect lines
      if (historicalData.length > 0 && forecastData.length > 0) {
        const lastHistoricalPoint = historicalData[historicalData.length - 1];
        forecastData.unshift(lastHistoricalPoint);
      }

      const baseConfig = {
        yAxisIndex,
        symbol: 'circle' as const,
        sampling: 'none' as const,
        itemStyle: {
          color,
        },
      };

      // Add historical data series
      if (historicalData.length > 0 || (historicalData.length === 0 && forecastData.length === 0)) {
        const historicalConfig = {
          ...baseConfig,
          name: sensorName,
          data: historicalData,
          lineStyle: {
            width: 2,
          },
        };

        switch (chartType) {
          case 'line':
            series.push({
              ...historicalConfig,
              type: chartType,
              symbolSize: filteredAxis[yAxisIndex]?.displayDataPoints ? 6 : 2,
              showSymbol: true,
              step: lineStyle === 'step' ? 'end' : undefined,
            });
            break;
          case 'bar':
          case 'scatter':
            series.push({
              ...historicalConfig,
              type: chartType,
              symbolSize: 6,
            });
            break;
          default:
            series.push({
              ...historicalConfig,
              type: 'line',
              symbolSize: 6,
              showSymbol: true,
            });
        }
      }

      // Add forecast data series with dotted line
      if (forecastData.length > 0) {
        const forecastConfig = {
          ...baseConfig,
          name: sensorName,
          data: forecastData,
          lineStyle: {
            width: 2,
            type: 'dashed' as const,
          },
        };

        switch (chartType) {
          case 'line':
            series.push({
              ...forecastConfig,
              type: chartType,
              symbolSize: filteredAxis[yAxisIndex]?.displayDataPoints ? 6 : 2,
              showSymbol: true,
            });
            break;
          case 'bar':
          case 'scatter':
            series.push({
              ...forecastConfig,
              type: chartType,
              symbolSize: 6,
            });
            break;
          default:
            series.push({
              ...forecastConfig,
              type: 'line',
              symbolSize: 6,
              showSymbol: true,
            });
        }
      }
    });
    return series;
  }

  createForecastMarkArea(filteredSensors: [string, any][], maxDate: Date): SeriesOption | null {
    const now = new Date();
    let forecastStart: number | null = null;
    let forecastEnd: number | null = null;

    // Find the forecast time range from all sensors
    filteredSensors.forEach(([, sensorData]) => {
      if (sensorData.data) {
        const historicalData = sensorData.data.filter((point: any) => new Date(point[0]) <= now);
        const forecastData = sensorData.data.filter((point: any) => new Date(point[0]) > now);

        if (historicalData.length > 0 && forecastData.length > 0) {
          // Start from last historical point
          const lastHistoricalPoint = historicalData[historicalData.length - 1];
          const start = this.toZonedMs(new Date(lastHistoricalPoint[0]));
          const end = this.toZonedMs(maxDate);

          if (forecastStart === null || start < forecastStart) {
            forecastStart = start;
          }
          if (forecastEnd === null || end > forecastEnd) {
            forecastEnd = end;
          }
        }
      }
    });

    if (forecastStart === null || forecastEnd === null) {
      return null;
    }

    return {
      type: 'line',
      name: '',
      data: [],
      markArea: {
        silent: true,
        itemStyle: {
          color: 'rgba(128, 128, 128, 0.1)',
        },
        data: [
          [
            {
              xAxis: forecastStart,
            },
            {
              xAxis: forecastEnd,
            },
          ],
        ],
      },
    };
  }

  createDstMarkLines(minDate: Date, maxDate: Date): SeriesOption | null {
    const tz = this.tz;
    if (!tz) return null;

    const startMs = minDate.getTime();
    const endMs = maxDate.getTime();
    const events = this.findDstTransitions(tz, startMs, endMs);

    if (events.length === 0) return null;

    const fmtOff = (ms: number) => {
      const sign = ms >= 0 ? '+' : '-';
      const abs = Math.abs(ms);
      const h = String(Math.floor(abs / 3_600_000)).padStart(2, '0');
      const m = String(Math.floor((abs % 3_600_000) / 60_000)).padStart(2, '0');
      return `UTC${sign}${h}:${m}`;
    };

    return {
      type: 'line',
      name: '',
      data: [],
      markLine: {
        silent: true,
        symbol: 'none',
        lineStyle: {
          type: 'dashed',
          color: '#9B9FA8',
          width: 1.5,
        },
        label: {
          show: true,
          position: 'end',
          fontSize: 10,
          color: '#9B9FA8',
          rotate: 0,
          offset: [0, 5],
          formatter: (params: any) => params.name,
        },
        data: events.map((e) => ({
          xAxis: this.toZonedMs(new Date(e.ms)),
          name: `DST switch from ${fmtOff(e.fromOffset)} to ${fmtOff(e.toOffset)}`,
        })),
      },
    };
  }

  createEmptySeries(filteredSensors: [string, any][], emptyLineColors: string[]): SeriesOption[] {
    return filteredSensors.map(
      (sensorName, index) =>
        ({
          name: sensorName[0],
          type: 'line',
          data: [],
          itemStyle: {
            color: emptyLineColors[index % emptyLineColors.length],
          },
        }) as SeriesOption
    );
  }

  createYAxes(
    filteredAxis: AxisSettings[],
    globalSettings: GlobalSettings,
    axisCount: number,
    axisSpacing: number,
    unitMinMax: Record<string, { min: number; max: number }>,
    chartIndex: number,
    filteredSensors: [string, any][] = []
  ): YAXisOption[] {
    return filteredAxis.map((axisSettings: AxisSettings, index) => {
      const axisIndex = globalSettings.chartMode === 'single' ? index : chartIndex;
      const minVal = unitMinMax[axisIndex]?.min ?? 0;
      const maxVal = unitMinMax[axisIndex]?.max ?? 1;

      // Get scaling factor from sensors for this axis
      const sensorsForAxis = filteredSensors.filter(([, sensorData]) => {
        const sensorAxisIndex = this.getAxisIndex(sensorData, filteredAxis, globalSettings);
        return sensorAxisIndex === index;
      });
      const scalingFactor = sensorsForAxis.length > 0 ? sensorsForAxis[0][1].scalingFactor : 1;
      const scaledMin = scalingFactor && scalingFactor !== 1 ? minVal / scalingFactor : minVal;
      const scaledMax = scalingFactor && scalingFactor !== 1 ? maxVal / scalingFactor : maxVal;
      // Use the same decimal precision for every tick on this axis, so labels line up
      // (e.g. 33.10 / 33.45 / 34.00) instead of each tick trimming its own trailing zeros.
      const axisPrecision = this.getSharedAxisPrecision([scaledMin, scaledMax]);

      return {
        type: 'value',
        name: axisSettings.name ? axisSettings.name : '',
        position: index % 2 === 0 ? 'left' : 'right',
        offset: Math.floor(index / 2) * axisSpacing,
        min: minVal,
        max: maxVal,
        scale: axisSettings.zeroYAxis ? false : true,
        alignTicks: false,
        axisLine: {
          show: true,
          lineStyle: { color: '#000000' },
        },
        splitLine: {
          show: index === 0 ? true : false,
        },
        axisLabel: {
          show: true,
          formatter: (value: number) => {
            const result = scalingFactor && scalingFactor !== 1 ? value / scalingFactor : value;
            return this.formatAxisTickLabel(result, axisPrecision);
          },
          color: '#9B9FA8',
          margin: 8,
          width: 45,
          height: 16,
          padding: 0,
          backgroundColor: '#fff',
        },
        nameTextStyle: {
          color: '#9B9FA8',
          align: 'center',
          verticalAlign: 'top',
          padding: [0, 0, 0, axisCount > 3 ? 5 : 0],
        },
        nameLocation: 'middle',
        nameGap: 68,
        nameRotate: index % 2 === 0 ? 90 : -90,
      };
    });
  }

  createToolboxConfig(
    chartIndex: number,
    yAxes: YAXisOption[],
    callbacks: {
      onSingleItemTool: (chartIndex: number) => void;
      onPanIndicator: (chartIndex: number) => void;
      onResetTool: (chartIndex: number) => void;
      onDownloadCSV: () => void;
    }
  ): any {
    return {
      show: true,
      feature: {
        mySingleItemTool: {
          show: true,
          title: 'Single Selection Tooltip',
          icon: 'path://M1 7C1 7.78793 1.15519 8.56815 1.45672 9.2961C1.75825 10.0241 2.20021 10.6855 2.75736 11.2426C3.31451 11.7998 3.97595 12.2417 4.7039 12.5433C5.43185 12.8448 6.21207 13 7 13M1 7C1 6.21207 1.15519 5.43185 1.45672 4.7039C1.75825 3.97595 2.20021 3.31451 2.75736 2.75736C3.31451 2.20021 3.97595 1.75825 4.7039 1.45672C5.43185 1.15519 6.21207 1 7 1M1 7H3.66667M7 13C7.78793 13 8.56815 12.8448 9.2961 12.5433C10.0241 12.2417 10.6855 11.7998 11.2426 11.2426C11.7998 10.6855 12.2417 10.0241 12.5433 9.2961C12.8448 8.56815 13 7.78793 13 7M7 13V11M13 7C13 6.21207 12.8448 5.43185 12.5433 4.7039C12.2417 3.97595 11.7998 3.31451 11.2426 2.75736C10.6855 2.20021 10.0241 1.75825 9.2961 1.45672C8.56815 1.15519 7.78793 1 7 1M13 7H11M7 1V3.66667',
          iconStyle: {
            borderColor: '#6e6e6e',
          },
          onclick: () => callbacks.onSingleItemTool(chartIndex),
        },
        myPanIndicator: {
          show: true,
          title: 'Pan',
          icon: 'path://M12 5v14M5 12h14M12 5l-3 3M12 5l3 3M12 19l-3-3M12 19l3-3M5 12l3-3M5 12l3 3M19 12l-3-3M19 12l-3 3',
          iconStyle: {
            color: '#36ADEF',
            borderColor: '#36ADEF',
          },
          emphasis: {
            iconStyle: {
              color: '#36ADEF',
              borderColor: '#36ADEF',
            },
          },
          onclick: () => callbacks.onPanIndicator(chartIndex),
        },
        dataZoom: {
          yAxisIndex: [...Array(yAxes.length).keys()],
          filterMode: 'none' as const,
          title: {
            zoom: 'Area Zoom',
            back: 'Undo Zoom',
          },
          icon: {
            zoom: 'path://M15 13v4 M13 15h4 M15 15m-5 0a5 5 0 1 0 10 0a5 5 0 1 0 -10 0 M22 22l-3 -3 M6 18h-1a2 2 0 0 1 -2 -2v-1 M3 11v-1 M3 6v-1a2 2 0 0 1 2 -2h1 M10 3h1 M15 3h1a2 2 0 0 1 2 2v1',
            back: 'path://M 21 21 l -6 -6 M 3.268 12.043 a 7.017 7.017 0 0 0 6.634 4.957 a 7.012 7.012 0 0 0 7.043 -6.131 a 7 7 0 0 0 -5.314 -7.672 a 7.021 7.021 0 0 0 -8.241 4.403 M 3 4 v 4 h 4',
          },
          show: true,
        },
        myResetTool: {
          show: true,
          title: 'Reset',
          icon: 'path://M 3.06 13 a 9 9 0 1 0 0.49 -4.087 M 3 4.001 v 5 h 5 m -1 0',
          onclick: () => callbacks.onResetTool(chartIndex),
        },
        saveAsImage: {
          title: 'Save as Image',
          name: `sensor-data-${this.formatCsvDate(new Date())}`,
          icon: 'path://M15 8h.01 M12.5 21h-6.5a3 3 0 0 1 -3 -3v-12a3 3 0 0 1 3 -3h12a3 3 0 0 1 3 3v6.5 M3 16l5 -5c.928 -.893 2.072 -.893 3 0l4 4 M14 14l1 -1c.653 -.629 1.413 -.815 2.13 -.559 M19 16v6M22 19l-3 3l-3 -3',
        },
        myTool: {
          show: true,
          title: 'Download CSV',
          icon: 'path:/M 12.5 21 h -7.5 a 2 2 0 0 1 -2 -2 v -14 a 2 2 0 0 1 2 -2 h 14 a 2 2 0 0 1 2 2 v 7.5 M 3 10 h 18 M 10 3 v 18 M 19 16 v 6 M 22 19 l -3 3 l -3 -3',
          onclick: () => callbacks.onDownloadCSV(),
        },
      },
      right: '8px',
    };
  }

  createDataZoomConfig(): any[] {
    return [
      {
        type: 'slider' as const,
        show: true,
        xAxisIndex: 0,
        start: 0,
        end: 100,
        zoomLock: false,
        moveHandleSize: 7,
        filterMode: 'none' as const,
        moveHandleStyle: {
          color: '#5470C6',
        },
        labelFormatter: (value: number) => this.formatDateWithTimezone(new Date(this.fromZonedMs(value))),
      },
      {
        type: 'inside' as const,
        xAxisIndex: 0,
        start: 0,
        end: 100,
        zoomOnMouseWheel: 'shift' as const,
        filterMode: 'none' as const,
        zoomLock: true,
        moveHandleSize: 7,
        moveHandleStyle: {
          color: '#5470C6',
        },
      },
      {
        type: 'slider' as const,
        show: false,
        yAxisIndex: 0,
        start: 0,
        end: 100,
        zoomLock: false,
        filterMode: 'none' as const,
        moveHandleSize: 7,
        moveHandleStyle: {
          color: '#419ac6',
        },
      },
      {
        type: 'inside' as const,
        yAxisIndex: 0,
        start: 0,
        end: 100,
        zoomOnMouseWheel: 'shift' as const,
        zoomLock: true,
        filterMode: 'none' as const,
        moveHandleSize: 7,
        moveHandleStyle: {
          color: '#419ac6',
        },
      },
    ];
  }

  getAxisIndex(
    sensorData: {
      data?: any[];
      key: any;
      unit: any;
      valueType?: string;
      computationType?: string;
      sensorType?: string;
      scalingFactor?: number;
    },
    axis: AxisSettings[],
    globalSettings: GlobalSettings
  ): number {
    if (globalSettings.groupBy === 'unit') {
      if (sensorData.valueType === 'position') {
        return axis.findIndex((axisItem) => axisItem.name === 'Position');
      }

      if (sensorData.unit === '') {
        return axis.findIndex((axisItem) => axisItem.name === this.sensorTypeFormatter.formatSensorType(sensorData.sensorType));
      }
      return axis.findIndex(
        (axisItem) => axisItem.unit === sensorData.unit && axisItem.scalingFactor === sensorData.scalingFactor
      );
    } else {
      return axis.findIndex((axisItem) => axisItem.key === sensorData.key);
    }
  }

  calculateMinMaxValues(
    sensors: Record<string, any>,
    displayAxis: AxisSettings[],
    globalSettings: GlobalSettings,
    chartNumber: number
  ): Record<string, { min: number; max: number }> {
    const unitMinMax: Record<string, { min: number; max: number }> = {};

    for (const sensor of Object.values(sensors)) {
      if (sensor?.data?.length > 0) {
        const yAxisIndex = this.getAxisIndex(sensor, displayAxis, globalSettings);

        if (!unitMinMax[yAxisIndex]) {
          unitMinMax[yAxisIndex] = { min: Infinity, max: -Infinity };
        }

        for (const point of sensor.data) {
          if (Array.isArray(point) && point.length >= 2) {
            const value = point[1];
            if (typeof value === 'number' && !isNaN(value)) {
              unitMinMax[yAxisIndex].min = Math.min(unitMinMax[yAxisIndex].min, value);
              unitMinMax[yAxisIndex].max = Math.max(unitMinMax[yAxisIndex].max, value);
            }
          }
        }
      }
    }

    // Add padding and apply zero Y-Axis setting
    for (const key of Object.keys(unitMinMax)) {
      if (unitMinMax[key].min === Infinity || unitMinMax[key].max === -Infinity) {
        unitMinMax[key] = { min: 0, max: 1 };
        continue;
      }

      if (displayAxis[key]?.zeroYAxis) {
        if (unitMinMax[key].min > 0) {
          unitMinMax[key].min = 0;
        } else if (unitMinMax[key].max < 0) {
          unitMinMax[key].max = 0;
        }
      }

      const range = unitMinMax[key].max - unitMinMax[key].min;
      const padding = Math.abs(range) * 0.05;
      if (unitMinMax[key].min !== 0) {
        unitMinMax[key].min -= padding;
      }
      if (unitMinMax[key].max !== 0) {
        unitMinMax[key].max += padding;
      }

      const rounded = this.roundMinMaxToRange(unitMinMax[key].min, unitMinMax[key].max, displayAxis[key]?.scalingFactor);
      unitMinMax[key].min = rounded.min;
      unitMinMax[key].max = rounded.max;
    }

    return unitMinMax;
  }

  calculateTimeRange(sensors: Record<string, any>, timeRange?: TimeRangeInterface | null): { minDate: Date; maxDate: Date } {
    let minDate: Date | null = new Date(timeRange?.value[0] || null);
    let maxDate: Date | null = new Date(timeRange?.value[1] || null);

    if (!minDate || !maxDate) {
      for (const sensor of Object.values(sensors)) {
        if (sensor?.data?.length > 0) {
          const firstPoint = sensor.data[0];
          if (Array.isArray(firstPoint) && firstPoint.length >= 1) {
            if (minDate === null || firstPoint[0] < minDate) {
              minDate = firstPoint[0];
            }
          }

          const lastPoint = sensor.data[sensor.data.length - 1];
          if (Array.isArray(lastPoint) && lastPoint.length >= 1) {
            if (maxDate === null || lastPoint[0] > maxDate) {
              maxDate = lastPoint[0];
            }
          }
        }
      }
    }

    if (!minDate || !maxDate) {
      console.warn('Could not determine valid time range for chart');
      minDate = new Date();
      maxDate = new Date();
    }

    return { minDate, maxDate };
  }

  filterAxisForChart(displayAxis: AxisSettings[], chartIndex: number, globalSettings: GlobalSettings): AxisSettings[] {
    return displayAxis.filter((item, index) => {
      if (globalSettings.chartMode === 'single') {
        return true;
      } else if (index === chartIndex) {
        return true;
      }
      return false;
    });
  }

  filterSensorsForChart(
    sensors: Record<string, any>,
    filteredAxis: AxisSettings[],
    globalSettings: GlobalSettings
  ): [string, any][] {
    return Object.entries(sensors).filter(([, sensorData]) => {
      if (globalSettings.chartMode === 'single') {
        return true;
      }

      if (globalSettings.groupBy === 'unit') {
        if (sensorData.valueType === 'position') {
          return filteredAxis[0].name === 'Position';
        }
        if (sensorData.unit === '') {
          return filteredAxis[0].name === this.sensorTypeFormatter.formatSensorType(sensorData.sensorType);
        }
        // Filter by both unit and scaling factor
        return sensorData.unit === filteredAxis[0].unit && sensorData.scalingFactor === filteredAxis[0].scalingFactor;
      }
      if (sensorData.valueType === 'position') {
        return sensorData.key === filteredAxis[0].key;
      }
      return sensorData.key === filteredAxis[0].key && sensorData.unit === filteredAxis[0].unit;
    });
  }

  syncZoomAcrossCharts(charts: echarts.ECharts[], sourceChartIndex: number): void {
    if (!charts || charts.length <= 1) return;

    const sourceChart = charts[sourceChartIndex];
    if (!sourceChart) return;

    const sourceOption = sourceChart.getOption();
    const sourceDataZoom = sourceOption.dataZoom as any[];

    for (let i = 0; i < charts.length; i++) {
      if (i === sourceChartIndex) continue;

      const targetChart = charts[i];
      if (!targetChart) continue;

      const targetOption = targetChart.getOption();
      const targetDataZoom = targetOption.dataZoom as any[];

      if (sourceDataZoom && targetDataZoom) {
        const sourceXDataZoom = sourceDataZoom.find((dz: any) => dz.xAxisIndex && dz.xAxisIndex.includes(0));
        const targetXDataZoom = targetDataZoom.find((dz: any) => dz.xAxisIndex && dz.xAxisIndex.includes(0));

        if (sourceXDataZoom && targetXDataZoom) {
          if (sourceXDataZoom.start !== undefined) {
            targetXDataZoom.start = sourceXDataZoom.start;
          }
          if (sourceXDataZoom.end !== undefined) {
            targetXDataZoom.end = sourceXDataZoom.end;
          }

          targetChart.setOption({ dataZoom: targetDataZoom });
        }
      }
    }
  }

  syncMouseWheelZoom(charts: echarts.ECharts[], sourceChartIndex: number): void {
    if (!charts || charts.length <= 1) return;

    const sourceChart = charts[sourceChartIndex];
    if (!sourceChart) return;

    const sourceOption = sourceChart.getOption();
    const sourceDataZoom = (sourceOption.dataZoom as any[])?.find((dz: any) => dz.xAxisIndex && dz.xAxisIndex.includes(0));

    if (sourceDataZoom) {
      for (let i = 0; i < charts.length; i++) {
        if (i === sourceChartIndex) continue;

        const targetChart = charts[i];
        if (!targetChart) continue;

        const targetOption = targetChart.getOption();
        const targetDataZoom = (targetOption.dataZoom as any[])?.find((dz: any) => dz.xAxisIndex && dz.xAxisIndex.includes(0));

        if (targetDataZoom) {
          targetDataZoom.start = sourceDataZoom.start;
          targetDataZoom.end = sourceDataZoom.end;
          targetChart.setOption({ dataZoom: targetOption.dataZoom });
        }
      }
    }
  }

  processChartData(
    sensors: Record<string, any>,
    axis: AxisSettings[],
    globalSettings: GlobalSettings,
    timeRange: TimeRangeInterface | null | undefined,
    chartNumber: number,
    chartIndex: number
  ): ChartProcessingResult {
    const displayAxis = [...axis].sort((a, b) => {
      if (a.zeroYAxis === b.zeroYAxis) return 0;
      return a.zeroYAxis ? -1 : 1;
    });

    const unitMinMax = this.calculateMinMaxValues(sensors, displayAxis, globalSettings, chartNumber);
    const { minDate, maxDate } = this.calculateTimeRange(sensors, timeRange);
    const filteredAxis = this.filterAxisForChart(displayAxis, chartIndex, globalSettings);
    const filteredSensors = this.filterSensorsForChart(sensors, filteredAxis, globalSettings);
    const hasData = filteredSensors.length > 0 && filteredSensors.some(([, sensorData]) => sensorData?.data?.length > 0);

    return {
      unitMinMax,
      minDate,
      maxDate,
      filteredAxis,
      filteredSensors,
      hasData,
    };
  }

  formatDateWithTimezone(date: Date): string {
    return this.normalizeUtcLabel(this.getDateFormatter().format(date));
  }

  formatAxisTickLabel(value: number, precision: number): string {
    return value.toFixed(precision);
  }

  getSharedAxisPrecision(values: number[]): number {
    const decimals = values.map((value) => this.getRequiredDecimals(value));
    return Math.max(...decimals, 1);
  }

  formatTooltipDateWithTimezone(date: Date): string {
    return this.normalizeUtcLabel(this.getTooltipDateFormatter().format(date));
  }

  getTimezoneName(): string {
    return this.tz || Intl.DateTimeFormat().resolvedOptions().timeZone;
  }

  // Header fields can contain user-defined custom names: quote them so commas, quotes and
  // line breaks stay inside one column, and prefix a single quote so spreadsheet apps don't
  // evaluate a leading =, +, -, @, tab or carriage return as a formula. Leading whitespace
  // (any Unicode space, plus zero-width space and BOM) is ignored for the =, +, -, @ check, as
  // some spreadsheet apps trim it before evaluating.
  escapeCsvHeaderField(field: string): string {
    const trimmed = field.replace(/^[\s\u200b]+/, '');
    const isFormula = /^[\t\r]/.test(field) || /^[=+\-@]/.test(trimmed);
    const neutralised = isFormula ? `'${field}` : field;
    return `"${neutralised.replace(/"/g, '""')}"`;
  }

  formatCsvTimestamp(date: Date): string {
    const tz = this.tz || Intl.DateTimeFormat().resolvedOptions().timeZone;
    const p = this.dateParts(date, tz, true);
    const offset = this.formatOffset(tz, date);
    return `${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}:${p.second}.${String(date.getMilliseconds()).padStart(
      3,
      '0'
    )}${offset}`;
  }

  formatCsvDate(date: Date): string {
    const tz = this.tz || Intl.DateTimeFormat().resolvedOptions().timeZone;
    const p = this.dateParts(date, tz, false);
    return `${p.year}-${p.month}-${p.day}`;
  }

  /** `timeZoneName: 'longOffset'` renders the UTC zone as bare "GMT" on some ICU builds and
   * "GMT+00:00" on others (CLDR data differs by Node/ICU version) — collapse both to "UTC" so the
   * label doesn't depend on which ICU build formatted it. */
  private normalizeUtcLabel(formatted: string): string {
    return formatted.replace('GMT+00:00', 'UTC').replace('GMT', 'UTC');
  }

  private getRequiredDecimals(value: number): number {
    const trimmed = value.toFixed(VALUE_PRECISION).replace(/0+$/, '');
    const dotIndex = trimmed.indexOf('.');
    return dotIndex === -1 ? 0 : trimmed.length - dotIndex - 1;
  }

  private roundMinMaxToRange(min: number, max: number, scalingFactor?: number): { min: number; max: number } {
    // The axis bounds computed here stay in raw (unscaled) units - createYAxes divides them by
    // scalingFactor afterward to render labels. Rounding the raw values to "nice" numbers doesn't
    // guarantee the displayed numbers are nice too (e.g. raw 118 / 3 = 39.333...), so round in
    // display (scaled) space first, then convert back - that keeps both ends clean.
    const effectiveScalingFactor = scalingFactor && scalingFactor !== 1 ? scalingFactor : 1;
    const scaledMin = min / effectiveScalingFactor;
    const scaledMax = max / effectiveScalingFactor;
    const scaledRange = Math.abs(scaledMax - scaledMin);

    // A flat/near-flat range has no meaningful magnitude to round to - rounding it would
    // only introduce an artificial gap, so leave it untouched.
    if (!isFinite(scaledRange) || scaledRange <= 1e-9) {
      return { min, max };
    }

    // Scale the rounding precision to the range's own order of magnitude, so a tight range
    // (e.g. 0.01) rounds to a fine step (e.g. 0.001) instead of always snapping to 1 decimal,
    // which would inflate a narrow range 10x and distort the chart.
    const minDecimals = 1;
    const maxDecimals = 6;
    // log10(range) is the range's order of magnitude (e.g. range=0.01 -> -2, range=10 -> 1).
    // Negating and ceiling it gives the number of decimal places at which the range stops
    // rounding to zero (0.01 -> 2 decimals), i.e. the precision needed to resolve it at all.
    const decimalsToResolveRange = Math.ceil(-Math.log10(scaledRange));
    const decimalsWithSafetyMargin = decimalsToResolveRange + 1;
    const decimals = Math.min(Math.max(decimalsWithSafetyMargin, minDecimals), maxDecimals);

    // scalingFactor could in principle be negative, which flips which scaled value is the lower
    // bound - re-sort before flooring/ceiling and again after converting back to raw units.
    const roundedScaledMin = this.floorToPrecision(Math.min(scaledMin, scaledMax), decimals);
    const roundedScaledMax = this.ceilToPrecision(Math.max(scaledMin, scaledMax), decimals);

    const a = roundedScaledMin * effectiveScalingFactor;
    const b = roundedScaledMax * effectiveScalingFactor;

    return {
      min: Math.min(a, b),
      max: Math.max(a, b),
    };
  }

  private floorToPrecision(value: number, decimals: number): number {
    const factor = 10 ** decimals;
    const scaled = value * factor;
    return Math.floor(scaled + Math.abs(scaled) * 1e-9 + 1e-9) / factor;
  }

  private ceilToPrecision(value: number, decimals: number): number {
    const factor = 10 ** decimals;
    const scaled = value * factor;
    return Math.ceil(scaled - Math.abs(scaled) * 1e-9 - 1e-9) / factor;
  }

  private findDstTransitions(tz: string, startMs: number, endMs: number): { ms: number; fromOffset: number; toOffset: number }[] {
    const DAY_30 = 30 * 24 * 3_600_000;
    const events: { ms: number; fromOffset: number; toOffset: number }[] = [];

    let prevOffset = getOffsetMs(tz, new Date(startMs));
    let t = startMs + DAY_30;

    while (t <= endMs + DAY_30) {
      const checkMs = Math.min(t, endMs);
      const offset = getOffsetMs(tz, new Date(checkMs));

      if (offset !== prevOffset) {
        // Narrow down with binary search to find exact transition
        let lo = t - DAY_30;
        let hi = checkMs;
        while (hi - lo > 1000) {
          const mid = Math.floor((lo + hi) / 2);
          if (getOffsetMs(tz, new Date(mid)) === prevOffset) {
            lo = mid;
          } else {
            hi = mid;
          }
        }
        const transitionOffset = getOffsetMs(tz, new Date(hi));
        events.push({ ms: hi, fromOffset: prevOffset, toOffset: transitionOffset });
        prevOffset = transitionOffset;
        // Continue scanning from just after the transition
        t = hi + DAY_30;
      } else {
        t += DAY_30;
      }

      if (checkMs >= endMs) break;
    }

    return events;
  }

  private getDateFormatter(): Intl.DateTimeFormat {
    const tz = this.tz || Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (this.cachedFormatter && this.cachedFormatterTz === tz) {
      return this.cachedFormatter;
    }
    this.cachedFormatterTz = tz;
    this.cachedFormatter = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      timeZoneName: 'longOffset',
    });
    return this.cachedFormatter;
  }

  private getTooltipDateFormatter(): Intl.DateTimeFormat {
    const tz = this.tz || Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (this.cachedTooltipFormatter && this.cachedTooltipFormatterTz === tz) {
      return this.cachedTooltipFormatter;
    }
    this.cachedTooltipFormatterTz = tz;
    this.cachedTooltipFormatter = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
      timeZoneName: 'longOffset',
    });
    return this.cachedTooltipFormatter;
  }

  private toZonedMs(date: Date): number {
    if (!this.tz) return date.getTime();
    return date.getTime() + getOffsetMs(this.tz, date);
  }

  private fromZonedMs(shiftedMs: number): number {
    if (!this.tz) return shiftedMs;
    return shiftedMs - getOffsetMs(this.tz, new Date(shiftedMs));
  }

  private dateParts(date: Date, tz: string, includeTime: boolean): Record<string, string> {
    const key = `${tz}:${includeTime}`;
    let fmt = this.cachedDatePartsFormatters.get(key);
    if (!fmt) {
      const opts: Intl.DateTimeFormatOptions = {
        timeZone: tz,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        ...(includeTime && { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false }),
      };
      fmt = new Intl.DateTimeFormat('en-US', opts);
      this.cachedDatePartsFormatters.set(key, fmt);
    }
    const parts = fmt.formatToParts(date);
    const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
    return {
      year: get('year'),
      month: get('month'),
      day: get('day'),
      hour: get('hour'),
      minute: get('minute'),
      second: get('second'),
    };
  }

  private formatOffset(tz: string, date: Date): string {
    const offsetMs = getOffsetMs(tz, date);
    const sign = offsetMs >= 0 ? '+' : '-';
    const abs = Math.abs(offsetMs);
    const h = String(Math.floor(abs / 3600000)).padStart(2, '0');
    const m = String(Math.floor((abs % 3600000) / 60000)).padStart(2, '0');
    return `${sign}${h}:${m}`;
  }
}
